import { DropdownOptionType } from '@Components/Dropdown/Type';
import { postFedExDetails } from '@Reducers/AdminShipment/actions';
import { AppDispatch } from '@Store';
import * as Yup from 'yup';

/* ----------------------------- OPTIONS ----------------------------- */

export const UpsPayorOptions: DropdownOptionType[] = [
  {
    value: 'SENDER',
    display: 'Sender',
  },
  {
    value: 'RECIPIENT',
    display: 'Recipient',
  },
  {
    value: 'THIRD_PARTY',
    display: 'Third Party',
  },
];

export const UpsServiceCodeOptions: DropdownOptionType[] = [
  {
    value: '01',
    display: 'UPS Next Day Air',
  },
  {
    value: '02',
    display: 'UPS 2nd Day Air',
  },
  {
    value: '03',
    display: 'UPS Ground',
  },
  {
    value: '07',
    display: 'UPS Express',
  },
  {
    value: '08',
    display: 'UPS Expedited',
  },
  {
    value: '11',
    display: 'UPS Standard',
  },
  {
    value: '12',
    display: 'UPS 3 Day Select',
  },
  {
    value: '65',
    display: 'UPS Express Saver',
  },
];

export const UpsPackagingCodeOptions: DropdownOptionType[] = [
  {
    value: '01',
    display: 'UPS Letter',
  },
  {
    value: '02',
    display: 'Customer Supplied Package',
  },
  {
    value: '03',
    display: 'Tube',
  },
  {
    value: '04',
    display: 'PAK',
  },
  {
    value: '21',
    display: 'Express Box',
  },
  {
    value: '24',
    display: '25KG Box',
  },
  {
    value: '25',
    display: '10KG Box',
  },
];

export const UpsReasonForExportOptions: DropdownOptionType[] = [
  {
    value: 'SALE',
    display: 'Sale',
  },
  {
    value: 'GIFT',
    display: 'Gift',
  },
  {
    value: 'SAMPLE',
    display: 'Sample',
  },
  {
    value: 'RETURN',
    display: 'Return',
  },
  {
    value: 'REPAIR',
    display: 'Repair',
  },
  {
    value: 'INTERCOMPANYDATA',
    display: 'Intercompany Data',
  },
];

export const UpsDocumentTypeOptions: DropdownOptionType[] = [
  { value: '001', display: 'Authorization Form' },
  { value: '002', display: 'Commercial Invoice' },
  { value: '003', display: 'Certificate of Origin' },
  { value: '004', display: 'Export Accompanying Document' },
  { value: '005', display: 'Export License' },
  { value: '006', display: 'Import Permit' },
  { value: '007', display: 'One Time NAFTA' },
  { value: '008', display: 'Other Document' },
  { value: '009', display: 'Power of Attorney' },
  { value: '010', display: 'Packing List' },
  { value: '011', display: 'SED Document' },
  { value: '012', display: "Shipper's Letter of Instruction" },
  { value: '013', display: 'Declaration' },
];

export const UpsFileFormatOptions: DropdownOptionType[] = [
  { value: 'pdf', display: 'PDF' },
  { value: 'doc', display: 'DOC' },
  { value: 'docx', display: 'DOCX' },
  { value: 'gif', display: 'GIF' },
  { value: 'jpg', display: 'JPG' },
  { value: 'png', display: 'PNG' },
  { value: 'txt', display: 'TXT' },
  { value: 'xls', display: 'XLS' },
  { value: 'xlsx', display: 'XLSX' },
];

export const getUpsDocumentTypeCode = (docNameOrCode?: string): string => {
  if (!docNameOrCode) return '008';
  const trimmed = docNameOrCode.trim().toLowerCase();
  const found = UpsDocumentTypeOptions.find(
    opt => opt.value.toLowerCase() === trimmed || opt.display.toLowerCase() === trimmed,
  );
  return found ? found.value : docNameOrCode;
};

export const getUpsDocumentTypeDisplay = (docNameOrCode?: string): string => {
  if (!docNameOrCode) return 'Document';
  const trimmed = docNameOrCode.trim().toLowerCase();
  const found = UpsDocumentTypeOptions.find(
    opt => opt.value.toLowerCase() === trimmed || opt.display.toLowerCase() === trimmed,
  );
  return found ? found.display : docNameOrCode;
};

export const getFileFormatFromDoc = (
  doc?: { name?: string; documentName?: string; contentType?: string } | null,
): string => {
  if (!doc) return 'pdf';
  const name = (doc.name || doc.documentName || '').toLowerCase();
  const contentType = (doc.contentType || '').toLowerCase();

  if (contentType.includes('pdf') || name.endsWith('.pdf')) return 'pdf';
  if (contentType.includes('wordprocessingml') || name.endsWith('.docx')) return 'docx';
  if (contentType.includes('msword') || name.endsWith('.doc')) return 'doc';
  if (contentType.includes('jpeg') || contentType.includes('jpg') || name.endsWith('.jpg') || name.endsWith('.jpeg'))
    return 'jpg';
  if (contentType.includes('png') || name.endsWith('.png')) return 'png';
  if (contentType.includes('gif') || name.endsWith('.gif')) return 'gif';
  if (contentType.includes('plain') || name.endsWith('.txt')) return 'txt';
  if (contentType.includes('spreadsheetml') || name.endsWith('.xlsx')) return 'xlsx';
  if (contentType.includes('excel') || name.endsWith('.xls')) return 'xls';
  return 'pdf';
};

/* --------------------------- INITIAL VALUES --------------------------- */

export interface upsInformationSchema {
  csp: string;
  type: string;
  accountNo: string;
  serviceCode: string;
  packagingCode: string;
  description: string;
  payor: string;
  ReasonforExport: string;
  awbShowToUser: boolean;
}

export const upsInitialValues: upsInformationSchema = {
  csp: 'movin_ups',
  type: '',
  accountNo: '',
  serviceCode: '',
  packagingCode: '',
  description: '',
  payor: '',
  ReasonforExport: '',
  awbShowToUser: true,
};

/* ----------------------------- VALIDATION ----------------------------- */

export const upsInformationValidation = Yup.object().shape({
  type: Yup.string().required('Type is required'),
  accountNo: Yup.mixed()
    .test('is-valid', 'Account is required', (value: any) => {
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'object') return true;
      return !!value;
    })
    .required('Account is required'),
  serviceCode: Yup.string()
    .transform(value => (Array.isArray(value) ? '' : value))
    .required('Service Code is required'),
  packagingCode: Yup.string()
    .transform(value => (Array.isArray(value) ? '' : value))
    .required('Packaging Code is required'),
  description: Yup.string().required('Description is required'),
  payor: Yup.string()
    .transform(value => (Array.isArray(value) ? '' : value))
    .required('Payor is required'),
  ReasonforExport: Yup.string()
    .transform(value => (Array.isArray(value) ? '' : value))
    .required('Reason for Export is required'),
});

/* ------------------------------- FORWARD ------------------------------- */

export const onForward = ({
  values,
  shipmentNumber,
  dispatch,
  nextFunc,
}: {
  values: upsInformationSchema;
  shipmentNumber: string;
  dispatch: AppDispatch;
  nextFunc: () => void;
}) => {
  const body = {
    ...JSON.parse(JSON.stringify(values)),
  };

  body.csp = 'movin_ups';

  for (const key in body) {
    if (body[key]) {
      if (key === 'accountNo') {
        body[key] = body[key]?.split('-')?.[0];
      }
    }
  }

  dispatch(
    postFedExDetails({
      shipmentNumber: `${shipmentNumber}`,
      body,
      date: null,
      nextFunc,
    }),
  );
};
